import { useMemo } from "react";

import type { ExtendedUIMessage } from "../chat.types";
import { composeSlotRenderMessages } from "../ephemeral-slot";
import { normalizeLiveTurn } from "../render-identity";
import type { AiSdkChatStatus } from "./action-for-stream-state-event";

/**
 * The server-authoritative render seam (brief 015). Composes the list the UI renders from:
 *
 * - **base** = persisted `serverHistory` + the render-only `heldResolvedTurns` (B-1445): turns the
 *   elicit resolve pulled out of the `useChat` buffer for a clean resume seed but whose authoritative
 *   row hasn't landed yet. Held turns carry their `renderKey`, so once `serverHistory` gets the real
 *   row the render-time dedupe collapses the pair (persisted first ⇒ wins).
 * - **slot** = the live `useChat` turns, each normalized to its canonical render form via
 *   `normalizeLiveTurn` (own parts + `renderKey`). While the SDK streams nothing is dropped; once a
 *   turn's persisted row appears in history it leaves the slot (`canDropTurnFromSlot`).
 *
 * `streamingTurn` (the live tail, ONLY while `status === "streaming"` — not `submitted`, where ai@6
 * has set the status before the first stream write) is passed to the composer so its live copy wins
 * over a stale/partial persisted twin (B-1406/B-1416/B-1443). `messages[0]` is the injected system
 * message and is dropped.
 */
export const useChatRenderList = ({
  serverHistory,
  heldResolvedTurns,
  messages,
  status,
}: {
  serverHistory: ExtendedUIMessage[];
  heldResolvedTurns: ExtendedUIMessage[];
  messages: ExtendedUIMessage[];
  status: AiSdkChatStatus;
}): ExtendedUIMessage[] =>
  useMemo(() => {
    const base = heldResolvedTurns.length > 0 ? [...serverHistory, ...heldResolvedTurns] : serverHistory;
    const liveTurns = messages.slice(1).map(normalizeLiveTurn);
    const streamingTurn = status === "streaming" ? liveTurns[liveTurns.length - 1] : undefined;
    return composeSlotRenderMessages(base, liveTurns, streamingTurn);
  }, [serverHistory, heldResolvedTurns, messages, status]);
